<template>
  <iColumn
    :width="width"
    gap="small"
    :style-overrides="containerStyleOverrides"
  >
    <iText
      v-if="hasPrompt"
      font-weight="dark"
      width="fill"
    >
      {{ promptText }}
    </iText>
    <iEditor
      ref="editor"
      variant="quickpost"
      :title-included="false"
      :is-loading="isLoading || isAttachingMedia"
      :placeholder="placeholderText"
      :upload-post-image="uploadImageFunction"
      @submit="createPost"
      @update:model-value="trackCharacterCount"
      @handle-link-modal-open="handleLinkModalOpen"
    />
    <iRow
      v-if="showsCounterRow"
      align="between"
      gap="small"
      vertical-align="middle"
      width="fill"
      wrap="nowrap"
    >
      <iText
        v-if="isOverLimit"
        font-size="extraSmall"
        variant="error"
      >
        {{ overLimitMessage }}
      </iText>
      <iSpace v-else />
      <iText :variant="counterVariant" font-size="extraSmall">
        {{ counterLabel }}
      </iText>
    </iRow>

    <teleport to="body">
      <RichTextDestinationPickerModal
        :show="showLinkModal"
        :original-link-value="originalLinkData.linkValue"
        :link-data="linkData"
        :publication-id="useFanPublicationStore().publicationId"
        :rich-text-ref="linkData.ref"
        @close="handleLinkModalClose"
        @update="linkData = {...linkData, ...$event}"
      />
    </teleport>
  </iColumn>
</template>

<script>
import { Enum } from "@vcl";
import { mapActions } from "pinia";
import { useFanPublicationStore } from "%/stores/fan-publication-store";
import { useConfigurationStore } from "%/stores/configuration-store";
import { useMediaUploadStore } from "%/stores/media-upload-store";
import { useMediaPlaybackStore } from "%/stores/media-playback-store";
import RichTextDestinationPickerModal from "%/components/RichTextDestinationPickerModal.vue";

const CHARACTER_COUNT_WARNING_FROM = 0.8;

export default {
  name: "QuickPost",
  components: { RichTextDestinationPickerModal },
  props: {
    width: {
      type: [Enum, Number],
      required: false,
      default: "fill",
      options: ["fill", "hug"],
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholderText: {
      type: String,
      required: false,
      default: "Write something amazing...",
    },
    maxLength: {
      type: Number,
      required: false,
      default: 0,
    },
    promptText: {
      type: String,
      required: false,
      default: "",
    },
    showCharacterCount: {
      type: Boolean,
      required: false,
      default: true,
    },
    imageAspectRatio: {
      type: Number,
      required: false,
      default: 0,
    },
    surface: {
      type: [Enum, String],
      required: false,
      default: "card",
      options: ["card", "plain"],
    },
  },
  emits: ["submit"],
  data() {
    return {
      characterCount: 0,
      attachedMediaId: 0,
      isAttachingMedia: false,
      pendingUpload: null,
      image: null,
      showLinkModal: false,
      linkData: {},
      originalLinkData: {},
    };
  },
  computed: {
    charactersRemaining() {
      return this.maxLength - this.characterCount;
    },
    isOverLimit() {
      return this.maxLength > 0 && this.characterCount > this.maxLength;
    },
    hasLengthLimit() {
      return this.maxLength > 0;
    },
    showsCounterRow() {
      return this.hasLengthLimit && this.showCharacterCount;
    },
    hasPrompt() {
      return this.promptText.length > 0;
    },
    isPlainSurface() {
      return this.surface === "plain";
    },
    tracksCharacterCount() {
      return this.characterCount > 0;
    },
    isNearingLimit() {
      return this.characterCount >= Math.floor(this.maxLength * CHARACTER_COUNT_WARNING_FROM);
    },
    counterVariant() {
      if (this.isOverLimit) {
        return "error";
      }
      return this.isNearingLimit ? "warning" : "subtle";
    },
    counterLabel() {
      if (!this.tracksCharacterCount) {
        return `Up to ${this.maxLength} characters`;
      }
      return `${this.characterCount} / ${this.maxLength}`;
    },
    overLimitMessage() {
      const over = this.characterCount - this.maxLength;
      return over === 1
        ? "1 character over the limit"
        : `${over} characters over the limit`;
    },
    containerStyleOverrides() {
      if (this.isPlainSurface && !this.isOverLimit) {
        return {};
      }
      return {
        borderWidth: "thin",
        borderColor: this.isOverLimit ? "striking" : "light",
        borderRadius: "large",
        backgroundColor: this.isPlainSurface ? "none" : "paper",
        paddingTop: "small",
        paddingBottom: "small",
        paddingLeft: "standard",
        paddingRight: "standard",
      };
    },
  },
  methods: {
    useFanPublicationStore,
    clearAttachedMedia() {
      this.attachedMediaId = 0;
    },
    ...mapActions(useConfigurationStore, [
      "isSignupAndLoginEnabled",
    ]),
    ...mapActions(useFanPublicationStore, [
      "uploadPostImage",
      "createPublicationPost",
    ]),
    trackCharacterCount(text) {
      this.characterCount = (text || "").length;
    },
    async createPost({ title, content, image }) {
      if (this.isOverLimit) {
        return;
      }
      if (image && this.pendingUpload) {
        // A failed upload must not swallow the author's text: attachMedia has
        // already logged it and cleared the id, so the post goes out as text.
        await this.pendingUpload.catch(() => {});
      }
      this.$emit("submit", { title, content, mediaId: image ? this.attachedMediaId : 0 });
    },
    reset() {
      this.$refs.editor.editorRef.reset();
      this.characterCount = 0;
      this.image = null;
      this.clearAttachedMedia();
    },
    uploadImageFunction(file) {
      this.isAttachingMedia = true;
      this.pendingUpload = this.attachMedia(file).finally(() => {
        this.isAttachingMedia = false;
      });
      return this.pendingUpload;
    },
    async attachMedia(file) {
      try {
        const mediaId = await useMediaUploadStore().uploadOneImage(file);
        this.attachedMediaId = mediaId;
        const url = await new Promise((resolve, reject) => {
          useMediaPlaybackStore().resolveMediaPlayback(mediaId, data => resolve(data.url), reject);
        });
        return { data: { imageUrl: url } };
      } catch (error) {
        this.attachedMediaId = 0;
        // eslint-disable-next-line no-console
        console.error("Quick Post image upload failed; the post will carry no image", error);
        throw error;
      }
    },
    handleLinkModalOpen(event) {
      this.linkData = event;
      this.originalLinkData = event;
      this.showLinkModal = true;
    },
    handleLinkModalClose() {
      this.linkData = {};
      this.originalLinkData = {};
      this.showLinkModal = false;
    },
  },
};
</script>
